import {
  CURVE,
  getPublicKey as starkGetPublicKey,
  sign as starkSign,
} from 'micro-starknet';
import {
  bytesToHex,
  concatBytes,
  hexToBytes,
  keccak256,
  stringToBytes,
} from 'viem';

import type { LimitOrder, Transfer } from '@sorare/crypto';
import {
  exportPublicKey,
  generateKey,
  signLimitOrder,
  signMessage,
  signTransfer,
} from '@sorare/crypto';

class StarkwareWallet {
  public static create() {
    const key = generateKey();

    return new StarkwareWallet(key);
  }

  public static load(privateKey: string) {
    const key = privateKey;
    return new StarkwareWallet(key);
  }

  private key: string;

  public constructor(key: string) {
    this.key = key;
  }

  public get publicKey() {
    return exportPublicKey(this.key, true);
  }

  public get publicKeyX() {
    return `0x${this.publicKey
      // Strip leading /0x0[23]/ to get X component
      .slice(4)}`;
  }

  public get privateKey() {
    return this.key;
  }

  public signLimitOrder(order: LimitOrder) {
    return signLimitOrder(this.privateKey, order);
  }

  public signTransfer(transfer: Transfer) {
    return signTransfer(this.privateKey, transfer);
  }

  public signRegisterEthAddress(ethAddress: string) {
    const ethBytes = hexToBytes(ethAddress as `0x${string}`);
    if (ethBytes.length !== 20) {
      throw new Error('ethAddress must be a 20-byte hex string');
    }
    const starkBytes = hexToBytes(
      `0x${this.publicKeyX.replace(/^0x/, '').padStart(64, '0')}`
    );
    const preimage = concatBytes([
      stringToBytes('UserRegistration:'),
      ethBytes,
      starkBytes,
    ]);
    const digest = BigInt(keccak256(preimage));
    const msgHash = (digest % CURVE.n).toString(16);
    const signature = starkSign(msgHash, this.privateKey.replace(/^0x/, ''));
    const r = signature.r.toString(16).padStart(64, '0');
    const s = signature.s.toString(16).padStart(64, '0');
    const uncompressed = starkGetPublicKey(
      this.privateKey.replace(/^0x/, ''),
      false
    );
    const starkKeyY = bytesToHex(uncompressed.slice(33)).replace(/^0x/, '');
    return `0x${r}${s}${starkKeyY}`;
  }

  public signWalletChallenge(challenge: string) {
    return signMessage(this.privateKey, `WALLET_CHALLENGE:${challenge}`);
  }

  public signMangopayWalletTransferAuthorizationRequest(request: {
    mangopayWalletId: string;
    operationHash: string;
    currency: string;
    amount: number;
    nonce: number;
  }) {
    return signMessage(
      this.privateKey,
      [
        request.mangopayWalletId,
        request.operationHash,
        request.currency,
        request.amount,
        request.nonce,
      ].join(':')
    );
  }
}

export default StarkwareWallet;
